import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getIdData, addData, updateData } from '../../lib/studentchecklist'
import { ChekListTypeSelect } from '../Common/FormSelects'
import { Breadcrumbs } from '../../AbstractElements'
import HeaderCard from '../Common/Component/HeaderCard'
import { Card, CardBody, Row, Col, FormGroup, Label, Button, Input } from 'reactstrap'

const URGENCY = {
	Must: 'Must',
	Should: 'Should',
	BetterHave: 'Better Have'
}

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const GlobalCheckListEdit = () => {
	const [startMonth, setStartMonth] = useState('')
	const [endMonth, setEndMonth] = useState('')
	const [urgency, setUrgency] = useState('Must')
	const [taskId, setTaskId] = useState('')
	const [tDate, setTDate] = useState('')
	const [isDone, setIsDone] = useState(false)
	const [isProgress, setIsProgress] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const navigate = useNavigate()
	const { slug, status, id } = useParams()

	const isAddMode = status === 'Add'

	const getData = async () => {
		if (!id || isAddMode) return

		try {
			setLoading(true)
			const checkListData = await getIdData(id)
			if (checkListData && checkListData.length > 0) {
				setStartMonth(checkListData[0].startMonth)
				setEndMonth(checkListData[0].endMonth)
				setUrgency(checkListData[0].urgency)
				setTaskId(checkListData[0].taskId)
				setTDate(checkListData[0].tDate)
				setIsDone(checkListData[0].isDone)
				setIsProgress(checkListData[0].isProgress)
			}
		} catch (error) {
			console.error('Error fetching checklist data:', error)
			setError('Failed to load checklist data')
		} finally {
			setLoading(false)
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)

		try {
			if (isAddMode) {
				const newTask = {
					studentId: slug,
					startMonth: startMonth,
					endMonth: endMonth,
					urgency: urgency,
					taskId: taskId,
					tDate: tDate,
					isDone: isDone,
					isProgress: isProgress
				}
				await addData(newTask)
			} else {
				const updatedTask = {
					id: id,
					studentId: slug,
					startMonth: startMonth,
					endMonth: endMonth,
					urgency: urgency,
					taskId: taskId,
					tDate: tDate,
					isDone: isDone,
					isProgress: isProgress
				}
				await updateData(updatedTask)
			}

			// Navigate back to previous page
			navigate(-1)
		} catch (error) {
			console.error('Error saving checklist item:', error)
			setError('Failed to save checklist item')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (!isAddMode) {
			getData()
		}
	}, [isAddMode, id])

	return (
		<>
			<Breadcrumbs
				parent="Student Management"
				title={isAddMode ? 'Add Checklist Item' : 'Update Checklist Item'}
				mainTitle={isAddMode ? 'Add Checklist Item' : 'Update Checklist Item'}
			/>
			<div className="container-fluid">
				<Row>
					<Col sm="12">
						<Card>
							<HeaderCard
								title={isAddMode ? 'Add Checklist Item' : 'Update Checklist Item'}
								subTitle={isAddMode ? 'Create a new checklist item for student' : 'Edit existing checklist item'}
							/>
							<CardBody>
								{error && (
									<div className="alert alert-danger alert-dismissible fade show" role="alert">
										<strong>Error:</strong> {error}
										<button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></button>
									</div>
								)}

								<form onSubmit={handleSubmit}>
									<Row>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Time Frame</Label>
												<div className="d-flex align-items-center gap-2">
													<Input type="select" value={startMonth} onChange={(e) => setStartMonth(e.target.value)} required disabled={loading}>
														<option value="">Start Month</option>
														{months.map((month, index) => (
															<option key={index} value={month}>
																{month}
															</option>
														))}
													</Input>
													<span>-</span>
													<Input type="select" value={endMonth} onChange={(e) => setEndMonth(e.target.value)} required disabled={loading}>
														<option value="">End Month</option>
														{months.map((month, index) => (
															<option key={index} value={month}>
																{month}
															</option>
														))}
													</Input>
												</div>
											</FormGroup>
										</Col>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Urgency</Label>
												<Input type="select" value={urgency} onChange={(e) => setUrgency(e.target.value)} required disabled={loading}>
													{Object.keys(URGENCY).map((key) => (
														<option key={key} value={key}>
															{URGENCY[key]}
														</option>
													))}
												</Input>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Task</Label>
												<ChekListTypeSelect value={taskId} setValue={setTaskId} />
											</FormGroup>
										</Col>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Date</Label>
												<Input type="date" value={tDate} onChange={(e) => setTDate(e.target.value)} required disabled={loading} />
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label className="form-control-label">Done</Label>
												<div className="form-check form-switch">
													<Input type="checkbox" checked={isDone} onChange={(e) => setIsDone(e.target.checked)} disabled={loading} />
												</div>
											</FormGroup>
										</Col>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">In Progress</Label>
												<div className="form-check form-switch">
													<Input type="checkbox" checked={isProgress} onChange={(e) => setIsProgress(e.target.checked)} disabled={loading} />
												</div>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col lg="12">
											<div className="d-flex justify-content-between">
												<Button type="submit" color="primary" disabled={loading}>
													{loading ? 'Saving...' : isAddMode ? 'Add Checklist Item' : 'Update Checklist Item'}
												</Button>
												<Button type="button" color="secondary" onClick={() => navigate(-1)} disabled={loading}>
													Cancel
												</Button>
											</div>
										</Col>
									</Row>
								</form>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</div>
		</>
	)
}

export { GlobalCheckListEdit }
